import { ArrowDown, ArrowUp } from "lucide-react";
import type { NormalizedTableData, TableColumn } from "@/types/table";
import { formatCurrency, formatNumber, formatPercent, formatSmartValue, titleizeKey } from "@/lib/tableFormatters";

type Row = Record<string, unknown>;

const arrayResponseKeys = ["data", "items", "results", "rows", "records"];
const rightAlignedTerms = ["hours", "amount", "gbp", "pct", "percent", "value", "total", "cor"];

function isObject(value: unknown): value is Row {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function flattenOneLevel(row: Row): Row {
  return Object.entries(row).reduce<Row>((acc, [key, value]) => {
    if (isObject(value)) {
      Object.entries(value).forEach(([childKey, childValue]) => {
        acc[`${key}_${childKey}`] = childValue;
      });
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
}

function extractRows(response: unknown): Row[] {
  if (Array.isArray(response)) return response.filter(isObject).map(flattenOneLevel);
  if (!isObject(response)) return [];

  for (const key of arrayResponseKeys) {
    const value = response[key];
    if (Array.isArray(value)) return value.filter(isObject).map(flattenOneLevel);
  }

  return Object.entries(response).map(([key, value]) => ({ field: titleizeKey(key), value }));
}

function createColumnsFromRows<T extends Row>(rows: T[]): TableColumn<T>[] {
  const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  return keys.map((key) => ({
    key,
    label: titleizeKey(key),
    align: shouldAlignRight(key) ? "right" : "left",
    format: (value) => formatSmartValue(value),
  }));
}

export function normalizeApiResponse(response: unknown): NormalizedTableData {
  const rows = extractRows(response);
  return { rows, columns: createColumnsFromRows(rows) };
}

function shouldAlignRight(key: string): boolean {
  const lowered = key.toLowerCase();
  return rightAlignedTerms.some((term) => lowered.includes(term));
}

function getMetricFormatter(type?: unknown) {
  if (type === "percent") return formatPercent;
  if (type === "currency") return formatCurrency;
  return formatNumber;
}

// The value columns get an up/down arrow, colored by the sign of a *sibling*
// variance metric for the same month -- not by their own value -- matching
// the Power BI reference: the arrow sits next to Utilisation %/Reported Hours,
// while Util Variance %/Variance render as plain numbers. Map: value metric
// key -> the variance metric key (same month) whose sign drives the icon.
// shortfall_hours = planned - reported, so a positive shortfall means
// reported fell short of plan -- that's bad (red), the opposite polarity of
// util_variance_pct (actual - planned, positive = good/green) -- hence invert.
const ICON_SOURCE_METRIC: Record<string, { key: string; invert?: boolean }> = {
  util_pct: { key: "util_variance_pct" },
  reported_hours: { key: "shortfall_hours", invert: true },
};

// sort_by value for each primary metric. Every month's column for this
// metric carries the same sort_by value -- the backend always ranks by
// that metric's value in the latest (rightmost) month currently in view
// regardless of which month's header triggered the sort, so all of a
// metric's monthly columns share one sort identity. Variance/shortfall
// sibling metrics are never sortable.
const SORT_KEY_BY_METRIC: Record<string, string> = {
  util_pct: "utilisation_percent",
  reported_hours: "reported_hours",
};

function formatValueWithVarianceIcon(value: unknown, rawVarianceValue: unknown, type?: unknown, invert?: boolean) {
  if (value === null || value === undefined || value === "") return "-";
  const formatted = getMetricFormatter(type)(value);

  const parsedVariance = Number(rawVarianceValue);
  if (!Number.isFinite(parsedVariance) || parsedVariance === 0) {
    return <span>{formatted}</span>;
  }
  const varianceNumber = invert ? -parsedVariance : parsedVariance;
  if (varianceNumber > 0) {
    return (
      <span className="inline-flex items-center justify-end gap-1 text-emerald-600 dark:text-emerald-400">
        <ArrowUp className="h-3 w-3 shrink-0" aria-hidden="true" />
        {formatted}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-end gap-1 text-red-600 dark:text-red-400">
      <ArrowDown className="h-3 w-3 shrink-0" aria-hidden="true" />
      {formatted}
    </span>
  );
}

export function normalizeKpiByOffice(response: unknown): NormalizedTableData {
  if (!isObject(response) || !Array.isArray(response.data)) return normalizeApiResponse(response);

  const officeList = Array.isArray(response.offices) ? response.offices.map(String) : getKeysFromNestedValues(response.data);

  const rows = response.data.filter(isObject).map((item) => {
    const values = isObject(item.values) ? item.values : {};
    return {
      metric: item.label ?? item.metric,
      total: item.total,
      metricType: item.type,
      ...Object.fromEntries(officeList.map((office) => [office, values[office]])),
    };
  });

  const columns: TableColumn<Row>[] = [
    { key: "metric", label: "Metric", align: "left" },
    {
      key: "total",
      label: "Affinia",
      align: "right",
      format: (value, row) => getMetricFormatter(row.metricType)(value),
      className: "bg-primary/35 text-foreground font-bold",
    },
    ...officeList.map<TableColumn<Row>>((office) => ({
      key: office,
      label: office,
      align: "right",
      format: (value, row) => getMetricFormatter(row.metricType)(value),
    })),
  ];

  return { rows, columns };
}

function getKeysFromNestedValues(items: unknown[]): string[] {
  return Array.from(
    new Set(
      items
        .filter(isObject)
        .flatMap((item) => (isObject(item.values) ? Object.keys(item.values) : [])),
    ),
  ).sort();
}

export function normalizeEmployeeMonthMatrix(response: unknown): NormalizedTableData {
  if (!isObject(response) || !Array.isArray(response.data) || !Array.isArray(response.months)) {
    return normalizeApiResponse(response);
  }

  const months = response.months.map(String);
  const metrics = Array.isArray(response.metrics) ? response.metrics.filter(isObject) : [];
  // One column per (month, metric) pair -- e.g. Utilisation % and the newer
  // Planned Util % / Util Variance % siblings all need their own column per
  // month, not just the first metric. Key/label convention mirrors the
  // backend Excel export's own "{month}__{metric}" / "{month} - {label}"
  // scheme (utilisation_drill_through_export.py) so the two line up.
  const columnKey = (month: string, metricKey: string) => `${month}__${metricKey}`;

  const rows = response.data.filter(isObject).map((employee) => {
    const byMonth = isObject(employee.by_month) ? employee.by_month : {};
    return {
      emp_name: employee.emp_name,
      emp_category: employee.emp_category,
      service_line: employee.service_line,
      legacy_office: employee.legacy_office,
      ...Object.fromEntries(
        months.flatMap((month) => {
          const monthValue = byMonth[month];
          return metrics.map((metric) => {
            const metricKey = String(metric.metric ?? "value");
            const value = isObject(monthValue) ? monthValue[metricKey] : undefined;
            return [columnKey(month, metricKey), value];
          });
        }),
      ),
    };
  });

  const columns: TableColumn<Row>[] = [
    { key: "emp_name", label: "Employee", align: "left", sortKey: "employee_name", sortType: "text" },
    { key: "emp_category", label: "Category", align: "left", sortKey: "category", sortType: "text" },
    { key: "service_line", label: "Service Line", align: "left", sortKey: "service_line", sortType: "text" },
    { key: "legacy_office", label: "Office", align: "left", sortKey: "office", sortType: "text" },
    ...months.flatMap((month) =>
      metrics.map<TableColumn<Row>>((metric) => {
        const metricKey = String(metric.metric ?? "value");
        const metricType = metric.type;
        const iconSource = ICON_SOURCE_METRIC[metricKey];
        const varianceColumnKey = iconSource ? columnKey(month, iconSource.key) : null;
        // Single-metric tables keep a clean month-only header, no grouping
        // needed. Multi-metric tables merge the month into one spanning
        // header cell above the individual metric names (UtilisationDataTable
        // renders the `group`), instead of repeating "{month} - " in every
        // column label.
        return {
          key: columnKey(month, metricKey),
          label: metrics.length > 1 ? String(metric.label ?? metricKey) : month,
          group: metrics.length > 1 ? month : undefined,
          align: "right",
          sortKey: SORT_KEY_BY_METRIC[metricKey],
          format: (value, row) =>
            varianceColumnKey
              ? formatValueWithVarianceIcon(value, row[varianceColumnKey], metricType, iconSource?.invert)
              : getMetricFormatter(metricType)(value),
        };
      }),
    ),
  ];

  return { rows, columns };
}
